import { z } from 'zod';
import { validateToolInputSchema } from '../tool-schema-validator';
import { uuidSchema } from './common';
import type { RekorToolKind } from './rekor-toolset';

/**
 * Hub pages (wayai-pro/wayai#5333): a hub's small working tools around its data. A page is one
 * self-contained HTML file, an MCP Apps view, and a manifest declaring who it is for and the base
 * toolset tools it calls. Hub config: held by the hub's HubDO (`page`, one row per page), pushed
 * to a preview and published to production like the rest of the hub.
 *
 * THE MANIFEST. `slug` names the page and never changes: relabel through `title`; a new slug is
 * a new page. `audience` is who the page is for, one of two (decision 9):
 *   - `team`: offered to holders of a `conversation:read` grant on the hub and to its team users, who
 *     run its write tools with a `conversation:write` grant, or as team users when `team_writes` is set;
 *   - `customers` (Phase 3): the hub's end users, each confined to their own records and shared ones.
 *     It writes only when `customer_writes` is set (opt-in), sets `show_in_conversation` (the
 *     conversation header is a customer's only standing way in), and its tools are `notify: none`
 *     (no customer page's call is noted for the agent yet).
 * Each audience's write flag is refused on the other's page. `tools` names each toolset tool the page calls, by its connection's name and
 * the tool's catalog name; a page addresses a tool by name alone (MCP Apps `tools/call`), so no
 * two entries share a tool name, across connections too. `notify` is the most the agent hears of
 * a call (`none` | `summary`). `params` is the JSON Schema (root `type: object`) of the values a
 * link may open the page with.
 *
 * THE HTML never travels with a summary (`pageSummarySchema`): a list of pages, and the hub config
 * a conversation caches, carry none of it.
 */

/** The most pages one hub holds. */
export const PAGES_PER_HUB_MAX = 20;
/** The most HTML one page holds, in UTF-8 bytes. */
export const PAGE_HTML_MAX_BYTES = 512 * 1024;

export const PAGE_SLUG_MAX = 64;
/** Lowercase letters, digits and single hyphens between them (e.g. `swim-results`). */
export const PAGE_SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const PAGE_TITLE_MAX = 120;
export const PAGE_DESCRIPTION_MAX = 500;
export const PAGE_ORDER_MAX = 1000;
export const PAGE_TOOLS_MAX = 50;
export const PAGE_CONNECTION_NAME_MAX = 200;
export const PAGE_TOOL_NAME_MAX = 128;
/** The most JSON text, in characters, a page's `params` schema takes. */
export const PAGE_PARAMS_MAX_CHARS = 16 * 1024;

/** The audiences a page may serve: its team, or its customers (#5333 Phase 3). */
export const PAGE_AUDIENCES = ['team', 'customers'] as const;
export type PageAudience = (typeof PAGE_AUDIENCES)[number];

/** The most the agent hears of a page's call to one tool (#5333 decision 10). */
export const PAGE_NOTIFY_LEVELS = ['none', 'summary'] as const;
export type PageNotifyLevel = (typeof PAGE_NOTIFY_LEVELS)[number];

export const pageSlugSchema = z.string().min(1).max(PAGE_SLUG_MAX).regex(PAGE_SLUG_REGEX, {
  message: 'a page slug is lowercase letters, digits and hyphens (e.g. "swim-results")',
});

export const pageToolSchema = z.object({
  /** The toolset connection's name, as `hub.yaml` names it. */
  connection: z.string().trim().min(1).max(PAGE_CONNECTION_NAME_MAX),
  /** The tool's name in its connection's catalog. */
  tool: z.string().trim().min(1).max(PAGE_TOOL_NAME_MAX),
  notify: z.enum(PAGE_NOTIFY_LEVELS).default('none'),
}).strict();

export type PageTool = z.output<typeof pageToolSchema>;

const pageParamsSchema = z.record(z.unknown()).superRefine((schema, ctx) => {
  if (JSON.stringify(schema).length > PAGE_PARAMS_MAX_CHARS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `params is at most ${PAGE_PARAMS_MAX_CHARS} characters of JSON` });
    return;
  }
  const result = validateToolInputSchema(schema);
  if (!result.ok) {
    for (const error of result.errors) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `params is not a valid JSON Schema${error.path ? ` at "${error.path}"` : ''}: ${error.message}`,
      });
    }
  }
});

/** The manifest's fields, before the rule that spans them (`pageManifestSchema`). */
const pageManifestObject = z.object({
  slug: pageSlugSchema,
  title: z.string().trim().min(1).max(PAGE_TITLE_MAX),
  description: z.string().trim().max(PAGE_DESCRIPTION_MAX).nullable().default(null),
  audience: z.enum(PAGE_AUDIENCES, {
    errorMap: () => ({ message: 'audience must be "team" or "customers"' }),
  }),
  /** A team page's: the hub's team users may run its write tools. A `conversation:write` grant on the hub always may. */
  team_writes: z.boolean().default(false),
  /**
   * A customer page's: its customers may run its write tools, each on their own records (opt-in). Stored
   * only when stated, so a page that states none (every team page) keeps the manifest it had.
   */
  customer_writes: z.boolean().optional(),
  /** The page is offered in the conversation header to whoever may open it. Required on a customer page. */
  show_in_conversation: z.boolean().default(false),
  /** Menu position, lowest first; pages of equal order sort by slug. */
  order: z.number().int().min(0).max(PAGE_ORDER_MAX).default(0),
  params: pageParamsSchema.nullable().default(null),
  tools: z.array(pageToolSchema).max(PAGE_TOOLS_MAX).default([]),
}).strict();

/**
 * A page's manifest, as pushed (`z.input`) and as stored (`z.output`, every default filled). Its
 * tool names are unique across the manifest, and each audience's own rules hold (THE MANIFEST above).
 */
export const pageManifestSchema = pageManifestObject.superRefine((manifest, ctx) => {
  if (manifest.audience === 'customers') {
    if (!manifest.show_in_conversation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['show_in_conversation'],
        message: 'a customer page sets show_in_conversation: true, since the conversation header is a customer\'s only way in',
      });
    }
    if (manifest.team_writes) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['team_writes'], message: 'team_writes is a team page\'s: a customer page writes only with customer_writes' });
    }
    if (manifest.tools.some((t) => t.notify !== 'none')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tools'],
        message: 'a customer page\'s tools are notify: none: the agent does not hear a customer page\'s calls yet',
      });
    }
  } else if (manifest.customer_writes) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['customer_writes'], message: 'customer_writes is a customer page\'s: a team page writes with team_writes' });
  }
  const seen = new Set<string>();
  manifest.tools.forEach((t, i) => {
    if (seen.has(t.tool)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tools', i, 'tool'],
        message: `two tools are named "${t.tool}": a page calls a tool by its name alone, so tool names are unique across the manifest`,
      });
    }
    seen.add(t.tool);
  });
});

export type PageManifestInput = z.input<typeof pageManifestSchema>;
export type PageManifest = z.output<typeof pageManifestSchema>;

/**
 * A page as a list or a read answers it: its manifest and what the hub stored with it, never its
 * HTML. `revision` changes whenever the manifest or the HTML does; a caller holding an older one
 * holds an older page.
 *
 * Read-side, so lenient where the manifest is strict: unknown keys are dropped at every level, and
 * `audience` and `notify` take any string. A reader built on it keeps working when a later server
 * answers a field, an audience or a notify level it does not know.
 */
export const pageSummarySchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  audience: z.string(),
  team_writes: z.boolean(),
  /** A customer page's opt-in to writes, when its manifest states it; absent otherwise. */
  customer_writes: z.boolean().optional(),
  show_in_conversation: z.boolean(),
  order: z.number(),
  params: z.record(z.unknown()).nullable(),
  tools: z.array(z.object({ connection: z.string(), tool: z.string(), notify: z.string() })),
  revision: z.string(),
  /** The HTML's size in UTF-8 bytes. */
  html_bytes: z.number().int().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** A summary as this server writes it: the stored manifest, every field of the contract's own type. */
export type PageSummary = PageManifest & Omit<z.output<typeof pageSummarySchema>, keyof PageManifest>;

/** One page with its HTML: what a single page's read answers. */
export type PageRecord = PageSummary & { html: string };

/** The UTF-8 size of `text`, counted without an encoder (React Native safe). */
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i += 1;
      } else {
        bytes += 3;
      }
    } else bytes += 3;
  }
  return bytes;
}

/** What is wrong with a page's HTML, or null: a non-empty string of at most `PAGE_HTML_MAX_BYTES`. */
export function pageHtmlIssue(html: unknown): string | null {
  if (typeof html !== 'string' || !/\S/.test(html)) return 'a page needs its HTML: one non-empty, self-contained file';
  const bytes = utf8ByteLength(html);
  if (bytes > PAGE_HTML_MAX_BYTES) {
    return `a page's HTML is at most ${PAGE_HTML_MAX_BYTES / 1024} KiB; this one is ${Math.ceil(bytes / 1024)} KiB`;
  }
  return null;
}

// =============================================
// The viewer routes and the page bridge (#5333 decisions 4, 6, 12)
// =============================================
//
// A page's viewers read it, and call its tools, through `/api/hubs/:hub_id/pages`. Only a person's own
// sign-in may (never a scoped token), and only for a page offered to them (decision 9), at one of two
// levels (decision 6, `PageViewerLevel`):
//   - a team page, to a viewer admitted to the hub's team pages (a `conversation:read` grant on the hub,
//     or a row on any of its teams);
//   - a customer page that sets `show_in_conversation`, to the hub's end users only (an enabled `hub_user`
//     row granting access), never through a grant or a team row, and only once the contact linked to their
//     account resolves for the hub (`PAGE_CUSTOMER_UNAVAILABLE_CODE` otherwise).

/** `GET /api/hubs/:hub_id/pages`. */
export const hubPagesParam = z.object({ hub_id: uuidSchema });

/**
 * `GET /api/hubs/:hub_id/pages?audience=`: which audience's pages the list answers. Absent or `team`, the
 * team pages, as every host before customer pages asks; `customers`, only the customer pages offered to the
 * viewer as an end user, so a host never shows them unless it asks for them by name.
 */
export const hubPagesQuery = z.object({ audience: z.enum(PAGE_AUDIENCES).optional() });

/** `GET /api/hubs/:hub_id/pages/:slug`, `POST /api/hubs/:hub_id/pages/:slug/tools/call`. */
export const hubPageParam = z.object({ hub_id: uuidSchema, slug: pageSlugSchema });

/** A page's revision as a summary carries it: SHA-256 of its manifest and HTML, hex. */
export const pageRevisionSchema = z.string().regex(/^[0-9a-f]{64}$/, { message: 'a page revision is 64 hex digits' });

/** The most JSON a page's tool call carries, in bytes; a larger body is refused with 413. */
export const PAGE_TOOL_CALL_BODY_MAX_BYTES = 1024 * 1024;

/**
 * One MCP Apps `tools/call` a page makes (decision 4): the tool by its name alone, as its manifest
 * declares it, its arguments, and the revision of the page the viewer holds. A deliberate exception
 * to the no-dispatch-body rule: the envelope is checked here, and the arguments against the tool's
 * schema in its connection's catalog.
 *
 * `conversation_id` names the conversation the page was opened over, when it was (decision 10). The
 * bridge only looks it up: a call to a tool whose `notify` is `summary` leaves a note there when the
 * viewer may send in that conversation as team, and is `none` otherwise. Absent, the call is `none`.
 */
export const pageToolCallBody = z.object({
  revision: pageRevisionSchema,
  tool: z.string().min(1).max(PAGE_TOOL_NAME_MAX),
  arguments: z.record(z.unknown()).default({}),
  conversation_id: uuidSchema.optional(),
}).strict();

export type PageToolCallBody = z.input<typeof pageToolCallBody>;

/**
 * The code of the refusal (409) a call carrying an older revision than the page's gets: the page was
 * pushed or published again since the viewer opened it, so its HTML is reloaded before any call
 * (decision 12).
 */
export const PAGE_REVISION_STALE_CODE = 'PAGE_REVISION_STALE';

/**
 * The code of the refusal (403) a customer page's read and every call of it get when no one customer can be
 * named for the viewer (#5333 decision 9): no contact of the org's contact book is linked to their account,
 * several are, or the one linked is outside the hub's scope tags or environment. Nothing is sent to the data
 * feature; a host shows the page as not available to their account. An expected state, so it is never
 * reported to Sentry (`SENTRY_SKIP_CODES`).
 */
export { PAGE_CUSTOMER_UNAVAILABLE_CODE } from '../errors';

/** `GET /api/hubs/:hub_id/pages`: the pages of the asked audience offered to the viewer, in menu order, never HTML. */
export interface HubPagesResponse {
  data: { pages: PageSummary[] };
}

/**
 * What a viewer may do on a page (decision 6): `write`, run every tool it declares; `read`, run only the
 * tools the toolset itself classifies as `read`. On a team page, `write` is a `conversation:write` grant on
 * the hub, or a team row when the page sets `team_writes`, and `read` everyone else the hub's team pages
 * admit. On a customer page, `write` is a page that sets `customer_writes`, and then only on a
 * customer-scoped toolset, on the viewer's own records: its other toolsets run reads only. The bridge
 * enforces it on every call; a host shows it.
 */
export const PAGE_VIEWER_LEVELS = ['read', 'write'] as const;
export type PageViewerLevel = (typeof PAGE_VIEWER_LEVELS)[number];

/**
 * A declared tool's kind as a page's read reports it: the toolset's own (`RekorToolKind`, a tool its
 * map does not name or names outside the vocabulary reported as `write`), or `unknown` when it could
 * not be read just then. Every kind but `read` may write. Advisory: the bridge reads the kinds again on
 * every call a `read` viewer makes, so a host refuses only a known `write`, `delete` or `composite` for
 * such a viewer and leaves `unknown` to the bridge (`@wayai/core/page-host` `pageToolGate`).
 */
export type PageToolKind = RekorToolKind | 'unknown';

/**
 * `GET /api/hubs/:hub_id/pages/:slug`: one page offered to the viewer, and its HTML. `viewer_level` and
 * `tool_kinds` (#5333 row 3.2B) are additive: a server before them sends neither, and offered a page
 * only at `write`; a reader that ignores them keeps working.
 */
export interface HubPageResponse {
  data: {
    page: PageSummary;
    html: string;
    viewer_level?: PageViewerLevel;
    /** Each tool the page declares, in its manifest's order, with its kind. */
    tool_kinds?: Array<{ tool: string; kind: PageToolKind }>;
  };
}

/**
 * `POST /api/hubs/:hub_id/pages/:slug/tools/call`: the tool's answer as an MCP `CallToolResult`. The
 * data feature's answer is `structuredContent` and its text; a refusal of the tool itself (the toolset
 * refused it, or its connection no longer reaches it) is `isError: true` with its text.
 */
export interface PageToolCallResponse {
  data: {
    content: Array<{ type: 'text'; text: string }>;
    structuredContent?: Record<string, unknown>;
    isError: boolean;
  };
}
